/**
 * Fait : la partie de l'écran de l'argent qui tient le CATALOGUE des modèles —
 *   la liste complète, éteintes comprises, l'inscription d'un modèle neuf, la
 *   retouche d'un nom ou d'un prix, et l'extinction.
 *
 *   ON N'EFFACE JAMAIS UNE ENTRÉE, on l'éteint : l'historique des dépenses
 *   renvoie aux modèles, et effacer rendrait illisibles des générations déjà
 *   payées. C'est aussi ce que fait le serveur — il n'a aucune route de
 *   suppression. Un modèle éteint reste ici, marqué, et disparaît du choix de
 *   TOUT LE MONDE, y compris de celui qui l'a éteint.
 *
 * Ne fait pas : elle n'appelle pas le serveur — l'écran qui la contient tient
 *   l'état — et n'invente aucun prix : ce qui n'a pas été relevé n'est pas
 *   affiché, plutôt que montré à zéro.
 */

import { useState } from 'react';

import { Partie } from '../gabarits/Partie.tsx';
import type { DemandeDeModele, EntreeDeCatalogueVue, RetoucheDeModele } from './api-argent.ts';
import { FormulaireDeModele, NOM_DU_FOURNISSEUR } from './FormulaireDeModele.tsx';
import { enEuros } from './montants.ts';

/** Le formulaire ouvert, s'il y en a un. */
type Redaction =
  null | { readonly quoi: 'neuf' } | { readonly quoi: 'retouche'; readonly id: string };

/** Ce qu'une entrée coûte, dit en une phrase — ou rien si elle ne coûte rien. */
function prixDit(entree: EntreeDeCatalogueVue): string {
  if (entree.voie === 'abonnement') return 'Compris dans un abonnement déjà payé.';

  const { prixEntreeCentimesParMillionDeJetons: envoi } = entree;
  const { prixSortieCentimesParMillionDeJetons: retour } = entree;

  if (envoi === undefined || retour === undefined) return 'Prix non relevé.';

  return `${enEuros(envoi)} par million de jetons envoyés, ${enEuros(retour)} par million reçus.`;
}

export function SectionDuCatalogue({
  modeles,
  enCours,
  surInscription,
  surRetouche,
}: {
  readonly modeles: readonly EntreeDeCatalogueVue[];
  readonly enCours: boolean;
  /**
   * Ces deux-là rendent le VERDICT du serveur, pas un accusé de clic : le
   * formulaire ne se referme qu'après un « oui ». Refermer au clic faisait
   * perdre toute la saisie chaque fois que le serveur refusait — on lisait
   * alors un refus sans plus rien à corriger. Observé lors de l'épreuve
   * indépendante du 2026-08-25.
   */
  readonly surInscription: (demande: DemandeDeModele) => Promise<boolean>;
  readonly surRetouche: (entreeId: string, demande: RetoucheDeModele) => Promise<boolean>;
}) {
  const [redaction, setRedaction] = useState<Redaction>(null);

  return (
    <Partie
      etiquette="Modèles"
      titre="Le catalogue des modèles"
      nature="travail"
      niveauDeTitre={3}
      aide={
        'Ce que la famille peut appeler. Un modèle éteint disparaît du choix de tout le ' +
        'monde ; il n’est jamais effacé, pour que les dépenses passées restent lisibles.'
      }
      data-role="partie-catalogue"
    >
      {redaction === null && (
        <div className="barre-de-boutons">
          <button
            type="button"
            data-role="inscrire-un-modele"
            onClick={() => setRedaction({ quoi: 'neuf' })}
          >
            Inscrire un modèle
          </button>
        </div>
      )}

      {redaction?.quoi === 'neuf' && (
        <FormulaireDeModele
          key="modele-neuf"
          enCours={enCours}
          surInscription={async (demande) => {
            const accepte = await surInscription(demande);
            if (accepte) setRedaction(null);
            return accepte;
          }}
          surRetouche={() => Promise.resolve(false)}
          surAbandon={() => setRedaction(null)}
        />
      )}

      {modeles.length === 0 ? (
        <p data-etat="vide" data-role="catalogue-vide">
          Aucun modèle au catalogue. Tant qu’il est vide, personne ne peut rien demander.
        </p>
      ) : (
        <ul className="empilement" data-liste="catalogue">
          {modeles.map((entree) => (
            <li key={entree.id} data-role="entree-de-catalogue" data-actif={String(entree.actif)}>
              <h4>
                {entree.libelle}
                {entree.actif ? '' : ' — éteint'}
              </h4>

              <p className="texte-attenue">
                {NOM_DU_FOURNISSEUR[entree.fournisseur]} — {entree.identifiantModele}
              </p>

              <p data-role="prix-de-l-entree">{prixDit(entree)}</p>

              {redaction?.quoi === 'retouche' && redaction.id === entree.id ? (
                <FormulaireDeModele
                  /* LA CLÉ PORTE L'ENTRÉE ET SA VERSION. Sans elle, passer de la
                     retouche d'un modèle à celle d'un autre garderait les champs
                     du premier — React réutilise l'instance, et les valeurs de
                     départ ne sont lues qu'au montage. On enregistrerait alors
                     le nom de A sur le modèle B, sans conflit et sans un mot.
                     Défaut réel, trouvé sur les consignes le 2026-08-25. */
                  key={`${entree.id}-${entree.version}`}
                  depart={entree}
                  enCours={enCours}
                  surInscription={() => Promise.resolve(false)}
                  surRetouche={async (entreeId, demande) => {
                    const accepte = await surRetouche(entreeId, demande);
                    if (accepte) setRedaction(null);
                    return accepte;
                  }}
                  surAbandon={() => setRedaction(null)}
                />
              ) : (
                <div className="barre-de-boutons">
                  <button
                    type="button"
                    data-role="retoucher-le-modele"
                    data-entree={entree.id}
                    onClick={() => setRedaction({ quoi: 'retouche', id: entree.id })}
                  >
                    Modifier
                  </button>

                  <button
                    type="button"
                    data-role="basculer-le-modele"
                    data-entree={entree.id}
                    disabled={enCours}
                    onClick={() => {
                      void surRetouche(entree.id, {
                        version: entree.version,
                        actif: !entree.actif,
                      });
                    }}
                  >
                    {entree.actif ? 'Éteindre' : 'Rallumer'}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Partie>
  );
}
